import { create, each, enforce, group, optional, test } from 'vest';
import FormState from 'admin-panel/resources/form-state';
import { WorkflowCode } from 'admin-panel/constants';
import { ITEM_LINK_URL_REGEX } from 'admin-panel/utils/regex';
import { localeValue } from 'admin-panel/components/panel/category/page-form';

import type IntlService from 'ember-intl/services/intl';
import type StoreService from '@ember-data/store';
import type ItemModel from 'admin-panel/models/item';
import type { WorkflowOptions } from 'admin-panel/models/item';
import type PriceModel from 'admin-panel/models/price';
import type IntegrationsRemoteReferenceModel from 'admin-panel/models/integrations/remote-reference';
import type { TranslatedField } from 'admin-panel/constants/types';

export type ItemFormPrice = { id?: string; value: string; currency_id: string };

export type ItemFormData = {
  workflow: string;
  title: string;
  teaser: string;
  active: boolean;
  visible: boolean;
  content: string;
  withLink: boolean;
  linkUrl: string;
  linkText: string;
  imageId: string | null;
  prices: ItemFormPrice[];
  maxQuantity?: WorkflowOptions['maxQuantity'];
  cron?: WorkflowOptions['cron'];
  maxPeopleCount?: WorkflowOptions['maxPeopleCount'];
  minPeopleCount?: WorkflowOptions['minPeopleCount'];
  integrationsRemoteReferences: string[];
  contentGroupId: string;
};

export class ItemFormState extends FormState<ItemFormData> {}

export const itemFormSuite = create<
  (data: ItemFormData, options: { t: IntlService['t'] }) => void
>('ItemForm', (data, { t }) => {
  optional({
    maxQuantity: !data.maxQuantity,
    maxPeopleCount: !data.maxPeopleCount,
    minPeopleCount: !data.minPeopleCount,
    linkUrl: !data.withLink,
    linkText: !data.withLink,
  });

  test('workflow', t('validations.required'), () => {
    enforce(data.workflow).isNotBlank();
  });

  test('title', t('validations.required'), () => {
    enforce(data.title).isNotBlank();
  });

  test('teaser', t('validations.length-lte', { length: 1000 }), () => {
    enforce(data.teaser).shorterThanOrEquals(1000);
  });

  each(data.prices, (field) => {
    const index = `${data.prices.indexOf(field)}`;

    test(`prices.${index}.value`, t('validations.required'), () => {
      enforce(field.value).isNotBlank();
    });

    test(`prices.${index}.value`, t('validations.number'), () => {
      enforce(field.value).isNumeric();
    });
  });

  group('workflowOptions', () => {
    test('maxQuantity', t('validations.number'), () => {
      enforce(data.maxQuantity).isNumeric();
    });
    test('maxPeopleCount', t('validations.number'), () => {
      enforce(data.maxPeopleCount).isNumeric();
    });
    test('minPeopleCount', t('validations.number'), () => {
      enforce(data.minPeopleCount).isNumeric();
    });
  });

  group('withLink', () => {
    test('linkUrl', t('validations.url'), () => {
      enforce(data.linkUrl).matches(ITEM_LINK_URL_REGEX);
    });

    test('linkText', t('validations.required'), () => {
      enforce(data.linkText).isNotBlank();
    });
  });
});

const COMMON_WORKFLOWS: string[] = [
  WorkflowCode.InformationItem,
  WorkflowCode.RequestItem,
  WorkflowCode.ReservationItem,
];

export function availableItemWorkflows(t: IntlService['t']) {
  const option = (code: string) => ({
    code,
    name: t(`panels.item.workflow-type.${code}`),
  });

  return {
    common: COMMON_WORKFLOWS.map(option),
    special: Object.values(WorkflowCode)
      .filter((code) => !COMMON_WORKFLOWS.includes(code))
      .map(option),
  };
}

export function pricesInCurrency(
  prices: PriceModel[],
  currencyId: string | undefined
) {
  return prices.filter(
    (price) =>
      !price.isDestroyed && price.belongsTo('currency').id() === currencyId
  );
}

export function newItemFormValues(): ItemFormData {
  return {
    workflow: WorkflowCode.InformationItem,
    title: '',
    teaser: '',
    active: true,
    visible: true,
    content: '',
    withLink: false,
    linkUrl: '',
    linkText: '',
    imageId: null,
    prices: [],
    maxQuantity: '',
    cron: '',
    maxPeopleCount: '',
    minPeopleCount: '',
    integrationsRemoteReferences: [],
    contentGroupId: '',
  };
}

export function initialItemFormValues(
  item: ItemModel,
  localeKey: string,
  prices: PriceModel[]
): ItemFormData {
  const linkUrl = localeValue(item.linkUrlTranslations ?? {}, localeKey);
  const linkText = localeValue(item.linkTextTranslations ?? {}, localeKey);

  return {
    workflow: item.workflow,
    title: localeValue(item.titleTranslations ?? {}, localeKey),
    teaser: localeValue(item.teaserTranslations ?? {}, localeKey),
    active: item.active,
    visible: item.visible,
    content: localeValue(item.contentTranslations ?? {}, localeKey),
    withLink: Boolean(linkUrl || linkText),
    linkUrl,
    linkText,
    imageId: item.belongsTo('image').id(),
    prices: prices.map((price) => ({
      id: price.id,
      value: price.value,
      currency_id: price.belongsTo('currency').id() as string,
    })),
    maxQuantity: item.workflowOptions?.maxQuantity || '',
    cron: item.workflowOptions?.cron || '',
    maxPeopleCount: item.workflowOptions?.maxPeopleCount || '',
    minPeopleCount: item.workflowOptions?.minPeopleCount || '',
    integrationsRemoteReferences: item
      .hasMany('integrationsRemoteReferences')
      .ids()
      .filter((id): id is string => id !== null),
    contentGroupId: item.belongsTo('contentGroup').id() ?? '',
  };
}

export function duplicateItemFormValues(
  source: ItemModel,
  localeKey: string,
  prices: PriceModel[]
): ItemFormData {
  const values = initialItemFormValues(source, localeKey, prices);

  return {
    ...values,
    prices: values.prices.map(({ value, currency_id }) => ({
      value,
      currency_id,
    })),
  };
}

const withLocale = (
  translations: TranslatedField | undefined,
  localeKey: string,
  value: string
): TranslatedField => ({ ...translations, [localeKey]: value });

export function itemTranslationsFrom(
  item: ItemModel,
  values: ItemFormData,
  localeKey: string
) {
  return {
    titleTranslations: withLocale(
      item.titleTranslations,
      localeKey,
      values.title
    ),
    teaserTranslations: withLocale(
      item.teaserTranslations,
      localeKey,
      values.teaser
    ),
    contentTranslations: withLocale(
      item.contentTranslations,
      localeKey,
      values.content
    ),
    linkUrlTranslations: withLocale(
      item.linkUrlTranslations,
      localeKey,
      values.withLink ? values.linkUrl : ''
    ),
    linkTextTranslations: withLocale(
      item.linkTextTranslations,
      localeKey,
      values.withLink ? values.linkText : ''
    ),
  };
}

export interface ItemPriceSyncResult {
  prices: ItemFormPrice[];
  failed: boolean;
}

export async function syncItemPrices(
  store: StoreService,
  item: ItemModel,
  existingPrices: PriceModel[],
  formPrices: ItemFormPrice[]
): Promise<ItemPriceSyncResult> {
  const formPriceFor = (price: PriceModel) =>
    formPrices.find((formPrice) => formPrice.id && formPrice.id === price.id);
  const updates = existingPrices.filter((price) => {
    const formPrice = formPriceFor(price);

    if (formPrice) price.value = formPrice.value;

    return Boolean(formPrice) && price.hasDirtyAttributes;
  });
  const deletions = existingPrices.filter((price) => !formPriceFor(price));
  const creations = formPrices
    .filter((formPrice) => !formPrice.id)
    .map((formPrice) => ({
      formPrice,
      record: store.createRecord('price', {
        value: formPrice.value,
        currency: store.peekRecord('currency', formPrice.currency_id),
        item,
      }),
    }));
  const operations = [
    ...updates.map((price) => ({ price, request: () => price.save() })),
    ...deletions.map((price) => ({
      price,
      request: () => price.destroyRecord(),
    })),
    ...creations.map(({ record }) => ({
      price: record,
      request: () => record.save(),
    })),
  ];
  const results = await Promise.allSettled(
    operations.map(({ request }) => request())
  );
  const failedPrices = operations
    .filter((_operation, index) => results[index]?.status === 'rejected')
    .map(({ price }) => price);

  failedPrices.forEach((price) => price.rollbackAttributes());

  return {
    prices: formPrices.map((formPrice) => {
      const creation = creations.find((entry) => entry.formPrice === formPrice);

      return creation && !failedPrices.includes(creation.record)
        ? { ...formPrice, id: creation.record.id }
        : formPrice;
    }),
    failed: failedPrices.length > 0,
  };
}

export interface ItemRelationshipSnapshot {
  imageId: string | null;
  contentGroupId: string | null;
  remoteReferenceIds: string[];
}

export function snapshotItemRelationships(
  item: ItemModel
): ItemRelationshipSnapshot {
  return {
    imageId: item.belongsTo('image').id(),
    contentGroupId: item.belongsTo('contentGroup').id(),
    remoteReferenceIds: item
      .hasMany('integrationsRemoteReferences')
      .ids()
      .filter((id): id is string => id !== null),
  };
}

export async function restoreItemRelationships(
  store: StoreService,
  item: ItemModel,
  snapshot: ItemRelationshipSnapshot
) {
  const [image, contentGroup] = await Promise.all([
    snapshot.imageId
      ? store.peekRecord('image', snapshot.imageId) ??
        store.findRecord('image', snapshot.imageId)
      : null,
    snapshot.contentGroupId
      ? store.peekRecord('content-group', snapshot.contentGroupId) ??
        store.findRecord('content-group', snapshot.contentGroupId)
      : null,
  ]);

  item.setProperties({ image, contentGroup });

  const currentReferenceIds = item
    .hasMany('integrationsRemoteReferences')
    .ids();

  if (
    currentReferenceIds.length === snapshot.remoteReferenceIds.length &&
    currentReferenceIds.every(
      (id, index) => id === snapshot.remoteReferenceIds[index]
    )
  ) {
    return;
  }

  const references = await item.integrationsRemoteReferences;

  references.setObjects(
    snapshot.remoteReferenceIds
      .map((id) => store.peekRecord('integrations/remote-reference', id))
      .filter(
        (reference): reference is IntegrationsRemoteReferenceModel =>
          reference !== null
      )
  );
}

export async function syncItemRemoteReferences(
  store: StoreService,
  item: ItemModel,
  hotelId: string,
  referenceIds: string[]
) {
  const currentIds = item
    .hasMany('integrationsRemoteReferences')
    .ids()
    .filter((id): id is string => id !== null);
  const idsToRemove = currentIds.filter((id) => !referenceIds.includes(id));
  const idsToAdd = referenceIds.filter((id) => !currentIds.includes(id));

  if (!idsToRemove.length && !idsToAdd.length) return;

  const [currentReferences, referencesToAdd] = await Promise.all([
    item.integrationsRemoteReferences,
    idsToAdd.length
      ? store.query('integrations/remote-reference', {
          filter: { hotel_id_eq: hotelId, id_in: idsToAdd },
        })
      : Promise.resolve([]),
  ]);

  idsToRemove.forEach((id) => {
    const reference = currentReferences.find((ref) => ref.id === id);

    if (reference) currentReferences.removeObject(reference);
  });

  idsToAdd.forEach((id) => {
    const reference = referencesToAdd.find((ref) => ref.id === id);

    if (reference) currentReferences.pushObject(reference);
  });
}
